<template>
  <div class="kb-wrap">
    <swiper class="kb-swiper" :options="opts">
      <swiper-slide v-for="(b, i) in slides" :key="i">
        <router-link :to="b.route" class="kb-slide" :class="'kb-slide--'+b.theme">
          <!-- Decorative floating motifs -->
          <div class="kb-motifs" aria-hidden="true">
            <span v-for="n in 6" :key="'m'+n" class="kb-motif" :style="motifStyle(n)">
              {{ b.motifs[(n-1) % b.motifs.length] }}
            </span>
          </div>

          <!-- Soft blobs for depth -->
          <div class="kb-blob kb-blob--1" aria-hidden="true"></div>
          <div class="kb-blob kb-blob--2" aria-hidden="true"></div>

          <!-- Content -->
          <div class="kb-body">
            <span class="kb-badge">
              <span class="kb-badge__dot"></span>{{ b.badge }}
            </span>
            <h3 class="kb-title">{{ b.title }}</h3>
            <p class="kb-sub">{{ b.sub }}</p>
            <span class="kb-cta">
              {{ b.cta }}
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
            </span>
          </div>

          <!-- Brand sigil bottom-right -->
          <div class="kb-brand" aria-hidden="true">
            <svg viewBox="0 0 40 40" fill="none">
              <path d="M20 34s-12-7.4-12-16a7 7 0 0 1 12-4.9A7 7 0 0 1 32 18c0 8.6-12 16-12 16z" fill="rgba(255,255,255,.65)"/>
            </svg>
            <span>KissMora</span>
          </div>
        </router-link>
      </swiper-slide>
      <div v-if="pagination !== false" class="swiper-pagination" slot="pagination"></div>
    </swiper>
  </div>
</template>

<script>
const DEFAULT_SLIDES = [
  {
    theme: 'rose',
    badge: 'Tonight',
    title: 'Find your kiss.',
    sub: "Meaningful connections, made simple.",
    cta: "Start exploring",
    route: '/Choose',
    motifs: ['💖','✨','🌹','💕']
  },
  {
    theme: 'sunset',
    badge: '✓ Verified',
    title: 'Real people. Real vibes.',
    sub: 'Every profile is verified by our team.',
    cta: 'See who\'s online',
    route: '/Choose',
    motifs: ['✨','💫','🔥','💖']
  },
  {
    theme: 'night',
    badge: '🎁 Daily reward',
    title: 'Log in. Get rewarded.',
    sub: 'Claim today\'s gift and keep the streak alive.',
    cta: 'Claim now',
    route: '/Lottery',
    motifs: ['🎁','⭐','💎','🎉']
  },
  {
    theme: 'aurora',
    badge: '👑 VIP',
    title: 'Unlock everything.',
    sub: 'Unlimited likes, see who liked you, priority matches.',
    cta: 'Go premium',
    route: '/Recharge',
    motifs: ['👑','💎','✨','💖']
  }
];

export default {
  name: 'KmBanners',
  props: {
    slides:    { type: Array, default: () => DEFAULT_SLIDES },
    autoplay:  { type: Number, default: 5000 },
    pagination:{ type: Boolean, default: true }
  },
  computed: {
    opts(){
      return {
        loop: (this.slides || []).length > 1,
        autoplay: this.autoplay > 0 ? { delay: this.autoplay, disableOnInteraction: false } : false,
        speed: 700,
        effect: 'slide',
        grabCursor: true,
        pagination: this.pagination ? { el: '.kb-wrap .swiper-pagination', clickable: true } : false
      };
    }
  },
  methods: {
    motifStyle(n){
      const x = [8, 78, 22, 88, 48, 68][n % 6];
      const y = [14, 22, 72, 62, 38, 82][n % 6];
      const s = [1, 0.7, 1.1, 0.85, 0.95, 0.75][n % 6];
      const d = [0, 1.2, 2.4, 3.1, 4.0, 5.0][n % 6];
      return {
        left: x + '%', top: y + '%',
        transform: 'scale(' + s + ')',
        animationDelay: d + 's'
      };
    }
  }
};
</script>

<style scoped>
.kb-wrap {
  max-width: 1180px; margin: 0 auto; padding: 14px 16px 0;
}
.kb-swiper {
  border-radius: 20px; overflow: hidden;
  box-shadow: 0 14px 32px rgba(160,32,240,.14);
}

.kb-slide {
  position: relative; display: block; overflow: hidden;
  aspect-ratio: 21 / 9; min-height: 180px;
  text-decoration: none; color: #fff;
  padding: 22px 22px 22px 26px;
  border-radius: 20px;
  isolation: isolate;
}

/* Theme backgrounds — bold brand gradients */
.kb-slide--rose {
  background:
    radial-gradient(circle at 80% 10%, rgba(255,255,255,.22), transparent 50%),
    radial-gradient(circle at 15% 90%, rgba(255,209,228,.35), transparent 55%),
    linear-gradient(135deg, #ff4d8d 0%, #a020f0 100%);
}
.kb-slide--sunset {
  background:
    radial-gradient(circle at 85% 15%, rgba(255,237,213,.3), transparent 55%),
    linear-gradient(120deg, #ff6b9d 0%, #ff4d8d 45%, #f72585 100%);
}
.kb-slide--night {
  background:
    radial-gradient(circle at 20% 20%, rgba(255,77,141,.45), transparent 55%),
    radial-gradient(circle at 80% 80%, rgba(160,32,240,.55), transparent 55%),
    linear-gradient(135deg, #1a1033 0%, #4a1a6e 55%, #7209b7 100%);
}
.kb-slide--aurora {
  background:
    radial-gradient(circle at 75% 25%, rgba(255,193,7,.22), transparent 55%),
    radial-gradient(circle at 15% 80%, rgba(160,32,240,.35), transparent 55%),
    linear-gradient(135deg, #5a189a 0%, #a020f0 50%, #ff4d8d 100%);
}

/* Floating motifs (emojis) */
.kb-motifs { position: absolute; inset: 0; pointer-events: none; }
.kb-motif {
  position: absolute;
  font-size: 28px; opacity: .28;
  filter: drop-shadow(0 4px 8px rgba(0,0,0,.25));
  animation: kbFloat 6s ease-in-out infinite;
}
@keyframes kbFloat {
  0%, 100% { transform: translate(0, 0) rotate(-4deg); }
  50%      { transform: translate(-6px, -8px) rotate(6deg); }
}

/* Decorative blobs for depth */
.kb-blob {
  position: absolute; border-radius: 50%; filter: blur(26px);
  pointer-events: none; opacity: .35;
}
.kb-blob--1 {
  width: 180px; height: 180px; top: -60px; right: -40px;
  background: rgba(255,255,255,.45);
  animation: kbBlob 11s ease-in-out infinite;
}
.kb-blob--2 {
  width: 140px; height: 140px; bottom: -40px; left: -30px;
  background: rgba(255,77,141,.5);
  animation: kbBlob 13s ease-in-out infinite reverse;
}
@keyframes kbBlob {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50%      { transform: translate(12px, -14px) scale(1.08); }
}

/* Body */
.kb-body {
  position: relative; z-index: 1;
  display: flex; flex-direction: column; align-items: flex-start; gap: 6px;
  max-width: 78%;
}
.kb-badge {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 5px 11px; border-radius: 999px;
  background: rgba(255,255,255,.2);
  border: 1px solid rgba(255,255,255,.28);
  backdrop-filter: blur(8px);
  font-size: 11px; font-weight: 700; letter-spacing: .04em;
  text-transform: uppercase;
}
.kb-badge__dot {
  width: 6px; height: 6px; border-radius: 50%; background: #fff;
  box-shadow: 0 0 8px rgba(255,255,255,.9);
  animation: kbPulse 1.6s ease-in-out infinite;
}
@keyframes kbPulse { 0%,100% { opacity: .5; } 50% { opacity: 1; } }

.kb-title {
  margin: 2px 0 0;
  font-size: 24px; font-weight: 800; letter-spacing: -.02em; line-height: 1.1;
  text-shadow: 0 2px 10px rgba(0,0,0,.28);
}
.kb-sub {
  margin: 0; font-size: 13px; opacity: .92; line-height: 1.4;
  max-width: 420px;
}
.kb-cta {
  display: inline-flex; align-items: center; gap: 6px;
  margin-top: 8px;
  padding: 8px 14px; border-radius: 999px;
  background: rgba(255,255,255,.95); color: #a020f0;
  font-size: 12px; font-weight: 800; letter-spacing: .01em;
  box-shadow: 0 8px 18px rgba(0,0,0,.18);
  transition: transform .15s ease, box-shadow .25s ease;
}
.kb-slide:hover .kb-cta {
  transform: translateY(-1px);
  box-shadow: 0 12px 24px rgba(0,0,0,.22);
}

/* Brand sigil */
.kb-brand {
  position: absolute; bottom: 14px; right: 16px; z-index: 1;
  display: inline-flex; align-items: center; gap: 6px;
  opacity: .8;
}
.kb-brand svg { width: 16px; height: 16px; }
.kb-brand span {
  font-size: 11px; font-weight: 800; letter-spacing: .02em;
  color: rgba(255,255,255,.85);
  text-shadow: 0 1px 6px rgba(0,0,0,.25);
}

/* Pagination dots */
.kb-wrap ::v-deep .swiper-pagination { bottom: 10px; }
.kb-wrap ::v-deep .swiper-pagination-bullet {
  background: rgba(255,255,255,.55); opacity: 1; width: 7px; height: 7px;
  transition: width .2s, background .2s;
}
.kb-wrap ::v-deep .swiper-pagination-bullet-active {
  background: #fff; width: 22px; border-radius: 4px;
}

/* Responsive */
@media (min-width: 720px) {
  .kb-wrap { padding: 18px 24px 0; }
  .kb-slide { padding: 28px 30px; min-height: 220px; }
  .kb-title { font-size: 32px; }
  .kb-sub   { font-size: 15px; }
  .kb-cta   { font-size: 13px; padding: 10px 18px; }
  .kb-motif { font-size: 34px; }
}
@media (min-width: 1024px) {
  .kb-slide { min-height: 260px; }
  .kb-title { font-size: 38px; }
}
@media (max-width: 360px) {
  .kb-slide { padding: 18px 18px; min-height: 160px; }
  .kb-title { font-size: 20px; }
  .kb-sub   { font-size: 12px; }
  .kb-cta   { font-size: 11px; padding: 6px 12px; }
  .kb-motif { font-size: 22px; opacity: .22; }
}
</style>
